// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';

import { StateAccordionItem } from './StateAccordionItem';
import type { CustomStatusEntityName, StateNameFor } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  opened: boolean;
  onClose: () => void;
  /** State name to expand by default when the modal opens (typically the entity's current state). */
  currentStateName?: StateNameFor<TEntity>;
};

/**
 * Modal that lets a user manage all custom statuses for an entity. Renders one
 * `StateAccordionItem` per state that supports custom statuses.
 */
export const ManageStatusesModal = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  opened,
  onClose,
  currentStateName,
}: Props<TEntity>) => {
  const { states, statesWithStatuses, pluralName } = useCustomStatuses(customStatusTypeName);

  const statesToRender = useMemo(
    () => states?.filter((state) => statesWithStatuses.includes(state.name)),
    [states, statesWithStatuses],
  );

  return (
    <WrappedModal
      opened={opened}
      onClose={onClose}
      title={`Manage statuses for ${pluralName.toLocaleLowerCase()}`}
      hideModalButtons
    >
      {statesToRender?.map((state) => (
        <StateAccordionItem
          key={state.stateId}
          customStatusTypeName={customStatusTypeName}
          state={state}
          defaultOpen={currentStateName === state.name}
        />
      ))}
    </WrappedModal>
  );
};
